import { useCallback, useEffect, useMemo, useState } from 'react';

// User-resizable, persisted table columns.
//
// Usage (define `columns` as a stable reference — a module constant or useMemo):
//   const COLS = [{ id: 'number', width: 80, min: 56 }, { id: 'item', width: 260 }, …];
//   const { totalWidth, colGroup, resizer } = useResizableColumns('po', COLS);
//   <div className="card overflow-x-auto">
//     <table className="text-sm table-fixed" style={{ width: totalWidth, minWidth: '100%' }}>
//       {colGroup}
//       <thead><tr>
//         <th className="relative …"><span className="block truncate pr-2">Label</span>{resizer(0)}</th>
//         …
//
// Widths persist to localStorage under `mpo.cols.<storageKey>`. Drag a handle to
// resize; double-click it to reset that column to its default width.
export function useResizableColumns(storageKey, columns, opts = {}) {
  const { resizerTitle } = opts;
  const lsKey = `mpo.cols.${storageKey}`;

  const [widths, setWidths] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(lsKey) || 'null');
      if (saved && typeof saved === 'object') {
        return columns.map((c) => Number(saved[c.id]) || c.width);
      }
    } catch { /* ignore corrupt storage */ }
    return columns.map((c) => c.width);
  });

  // Keep the widths array length in sync if the column set changes (e.g. an
  // admin-only column appears), reusing any saved width for surviving columns.
  useEffect(() => {
    setWidths((prev) => {
      if (prev.length === columns.length) return prev;
      return columns.map((c, i) => prev[i] ?? c.width);
    });
  }, [columns]);

  useEffect(() => {
    const map = {};
    columns.forEach((c, i) => { map[c.id] = widths[i]; });
    try { localStorage.setItem(lsKey, JSON.stringify(map)); } catch { /* ignore */ }
  }, [widths, lsKey, columns]);

  const startResize = useCallback((index, e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = widths[index];
    const min = columns[index]?.min ?? 56;
    const onMove = (ev) => {
      const dx = ev.clientX - startX;
      setWidths((prev) => {
        const next = [...prev];
        next[index] = Math.max(min, startW + dx);
        return next;
      });
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [widths, columns]);

  const resetColumn = useCallback((index) => {
    setWidths((prev) => {
      const next = [...prev];
      next[index] = columns[index].width;
      return next;
    });
  }, [columns]);

  const totalWidth = useMemo(() => widths.reduce((a, b) => a + b, 0), [widths]);

  const colGroup = (
    <colgroup>
      {widths.map((w, i) => <col key={columns[i]?.id ?? i} style={{ width: `${w}px` }} />)}
    </colgroup>
  );

  // Drag handle for the right edge of column `index`. The parent <th> must be
  // `relative`. The last column is typically left without one.
  const resizer = (index) => (
    <span
      role="separator"
      aria-orientation="vertical"
      onMouseDown={(e) => startResize(index, e)}
      onDoubleClick={() => resetColumn(index)}
      title={resizerTitle}
      className="absolute top-0 right-0 z-10 h-full w-1.5 cursor-col-resize select-none
                 bg-transparent hover:bg-cyan-500/60 active:bg-cyan-500 transition-colors"
    />
  );

  return { widths, totalWidth, colGroup, resizer, resetColumn };
}
